import { PermissionEnum, Role } from '@sr-sdks/api-sdk-axios';

// Base role that all users will have
const BASE_ROLE = 'Authenticated User';

// Define role hierarchy for inheritance
export const roleHierarchy: Record<string, string[]> = {
  [BASE_ROLE]: [],
  'System Admin': ['Service Specialist'],
  'Workspace Admin': ['Workspace User'],
  'Workspace Owner': ['Workspace Admin', 'Workspace User'],
  'Workspace User': [],
};

// Define role display name mapping (null means don't display)
export const roleDisplayMapping: Record<string, null | string> = {
  'Authenticated User': null,
  'Data Team System Process': null,
  'System Process': null,
  'Workspace Admin': 'Admin',
  'Workspace Owner': 'Owner',
  'Workspace User': 'Standard',
};

// Define the desired order of roles
export const roleOrder = [
  'Workspace User', // 1
  'Workspace Admin', // 2
  'Workspace Owner', // 3
];

// Formats a permission key into a more readable display name
export const formatPermissionName = (permissionKey: string): string => {
  if (!permissionKey) return '';

  // Replace hyphens and dots with spaces
  let formatted = permissionKey.replace(/[-_.]/g, ' ');

  // Remove any duplicate spaces
  formatted = formatted.replace(/\s+/g, ' ').trim();

  // Capitalize first letter of each word
  formatted = formatted
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return formatted;
};

// Checks if a role has a specific permission (directly or through inheritance)
export const hasPermission = (
  roleDisplayName: string,
  permissionKey: PermissionEnum,
  roles: Role[] | undefined,
): boolean => {
  if (!roles) return false;

  // Get the current role
  const role = roles.find((r) => r.displayName === roleDisplayName);

  if (!role) return false;

  // Check if the role directly has the permission
  if (role.permissionKeys.includes(permissionKey)) {
    return true;
  }

  // Get inherited roles and always include BASE_ROLE
  const inheritedRoles = [...(roleHierarchy[roleDisplayName] || [])];

  for (const inheritedRoleName of inheritedRoles) {
    const inheritedRole = roles.find(
      (r) => r.displayName === inheritedRoleName,
    );

    if (inheritedRole?.permissionKeys.includes(permissionKey)) {
      return true;
    }
  }

  return false;
};

// Gets the display name for a role based on the roleDisplayMapping
export const getDisplayName = (originalName: string): null | string => {
  // If the role is explicitly mapped to null, return null
  if (roleDisplayMapping[originalName] === null) {
    return null;
  }

  return roleDisplayMapping[originalName] ?? originalName;
};

// Filters and sorts roles based on the predefined order
export const getSortedRoles = (roles: Role[] | undefined): Role[] => {
  if (!roles) return [];

  return [...roles]
    .filter(
      (role) =>
        // Only include roles that don't have a null display name
        getDisplayName(role.displayName) !== null,
    )
    .sort((a, b) => {
      const aIndex = roleOrder.indexOf(a.displayName);
      const bIndex = roleOrder.indexOf(b.displayName);

      // If both roles are in our predefined order, sort by that order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one role is in our predefined order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Otherwise, sort alphabetically
      return a.displayName.localeCompare(b.displayName);
    });
};

// Parses a comma-separated string of hidden permissions into an array
export const parseHiddenPermissions = (
  hiddenPermissionList?: string,
): string[] => {
  return hiddenPermissionList
    ? hiddenPermissionList.split(',').map((perm: string) => perm.trim())
    : [];
};

// Gets all unique permissions from roles, filtered by hidden permissions
export const getAllPermissions = (
  roles: Role[] | undefined,
  hiddenPermissions: string[],
): string[] => {
  if (!roles) return [];

  return Array.from(new Set(roles.flatMap((role) => role.permissionKeys)))
    .filter((permission) => !hiddenPermissions.includes(permission))
    .sort((a, b) => {
      // For hyphenated keys like "edit-notification-preferences"
      // Extract the action part (first segment before the hyphen)
      const [aAction = ''] = a.split('-');
      const [bAction = ''] = b.split('-');

      // Group by action first (edit, view, etc.), then alphabetically
      return aAction === bAction
        ? a.localeCompare(b)
        : aAction.localeCompare(bAction);
    });
};
